import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Building2, TrendingUp, DollarSign, Calendar, 
  Mail, Phone, MapPin, Star, Award, ChevronRight, Filter,
  BarChart3, PieChart as PieChartIcon, Activity
} from 'lucide-react';

interface ClientData {
  id: number;
  name: string;
  type: string;
  importance: string;
  revenue?: number;
  deals?: number;
  lastContact?: string;
  status?: string;
  location?: string;
  industry?: string;
}

interface ClientAnalyticsGridProps {
  title: string;
  clients: ClientData[];
}

const ClientAnalyticsGrid: React.FC<ClientAnalyticsGridProps> = ({ title, clients }) => {
  const [selectedClient, setSelectedClient] = useState<number | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'revenue' | 'importance'>('revenue');

  // Generate mock data for clients
  const enrichedClients = clients.map((client, index) => ({
    ...client,
    revenue: client.revenue || Math.floor(Math.random() * 500000) + 50000,
    deals: client.deals || Math.floor(Math.random() * 20) + 1,
    lastContact: client.lastContact || `${Math.floor(Math.random() * 30) + 1} days ago`,
    status: client.status || ['Active', 'Pending', 'Negotiating'][Math.floor(Math.random() * 3)],
    location: client.location || ['New York', 'San Francisco', 'Chicago', 'Boston'][Math.floor(Math.random() * 4)],
    industry: client.industry || ['Technology', 'Finance', 'Healthcare', 'Retail'][Math.floor(Math.random() * 4)],
  }));

  // Filter and sort
  const filteredClients = enrichedClients
    .filter(c => filterType === 'all' || c.type === filterType)
    .sort((a, b) => {
      if (sortBy === 'revenue') return (b.revenue || 0) - (a.revenue || 0);
      if (sortBy === 'importance') return b.importance.localeCompare(a.importance);
      return a.name.localeCompare(b.name);
    });

  const getImportanceColor = (importance: string) => {
    const colors: Record<string, string> = {
      'High': 'from-red-500 to-orange-500',
      'Medium': 'from-yellow-500 to-amber-500',
      'Low': 'from-blue-500 to-cyan-500',
    };
    return colors[importance] || 'from-gray-500 to-gray-600';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'Active': 'bg-green-100 text-green-700 border-green-300',
      'Pending': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'Negotiating': 'bg-blue-100 text-blue-700 border-blue-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  // Calculate statistics
  const totalRevenue = filteredClients.reduce((sum, c) => sum + (c.revenue || 0), 0);
  const totalDeals = filteredClients.reduce((sum, c) => sum + (c.deals || 0), 0);
  const avgRevenue = filteredClients.length > 0 ? totalRevenue / filteredClients.length : 0;

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
          
          {/* Filters */}
          <div className="flex items-center gap-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500"
            >
              <option value="revenue">Sort by Revenue</option>
              <option value="importance">Sort by Importance</option>
              <option value="name">Sort by Name</option>
            </select>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="Company">Companies</option>
              <option value="Individual">Individuals</option>
            </select>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200"
          >
            <Users className="w-8 h-8 text-blue-600 mb-2" />
            <div className="text-2xl font-bold text-gray-900">{filteredClients.length}</div>
            <div className="text-sm text-gray-600">Total Clients</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200"
          >
            <DollarSign className="w-8 h-8 text-green-600 mb-2" />
            <div className="text-2xl font-bold text-gray-900">
              ${(totalRevenue / 1000000).toFixed(2)}M
            </div>
            <div className="text-sm text-gray-600">Total Revenue</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200"
          >
            <Award className="w-8 h-8 text-purple-600 mb-2" />
            <div className="text-2xl font-bold text-gray-900">{totalDeals}</div>
            <div className="text-sm text-gray-600">Total Deals</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200"
          >
            <TrendingUp className="w-8 h-8 text-amber-600 mb-2" />
            <div className="text-2xl font-bold text-gray-900">
              ${(avgRevenue / 1000).toFixed(0)}K
            </div>
            <div className="text-sm text-gray-600">Avg Revenue</div>
          </motion.div>
        </div>
      </div>

      {/* Client Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredClients.map((client, index) => {
          const isSelected = selectedClient === client.id;

          return (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => setSelectedClient(isSelected ? null : client.id)}
              className="cursor-pointer"
            >
              <motion.div
                whileHover={{ scale: 1.03, y: -5 }}
                className={`
                  bg-white rounded-2xl p-6 shadow-lg border-2 transition-all duration-300
                  ${isSelected ? 'border-blue-500 ring-4 ring-blue-100' : 'border-gray-200 hover:border-blue-300'}
                `}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getImportanceColor(client.importance)}
                      flex items-center justify-center shadow-lg`}>
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg truncate max-w-[150px]">
                        {client.name}
                      </h4>
                      <p className="text-xs text-gray-500">{client.type}</p>
                    </div>
                  </div>

                  {/* Importance Badge */}
                  <div className={`px-2 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${getImportanceColor(client.importance)} text-white`}>
                    {client.importance}
                  </div>
                </div>

                {/* Status */}
                <div className="mb-4">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(client.status || '')}`}>
                    <Activity className="w-3 h-3" />
                    {client.status}
                  </span>
                </div>

                {/* Revenue & Deals */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3 text-center">
                    <DollarSign className="w-5 h-5 mx-auto text-green-600 mb-1" />
                    <div className="text-lg font-bold text-gray-900">
                      ${((client.revenue || 0) / 1000).toFixed(0)}K
                    </div>
                    <div className="text-xs text-gray-600">Revenue</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-3 text-center">
                    <Award className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                    <div className="text-lg font-bold text-gray-900">{client.deals}</div>
                    <div className="text-xs text-gray-600">Deals</div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="space-y-2 text-xs text-gray-600">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span>{client.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span>{client.industry}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>Last contact: {client.lastContact}</span>
                  </div>
                </div>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isSelected && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mt-4 pt-4 border-t border-gray-200 overflow-hidden"
                    >
                      <div className="space-y-3">
                        {/* Performance Chart */}
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs font-semibold text-gray-700 mb-2">
                            Revenue Trend (6 months)
                          </div>
                          <div className="flex items-end gap-1 h-16">
                            {Array.from({ length: 6 }).map((_, i) => {
                              const height = Math.random() * 100;
                              return (
                                <motion.div
                                  key={i}
                                  className="flex-1 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t"
                                  initial={{ height: 0 }}
                                  animate={{ height: `${height}%` }}
                                  transition={{ duration: 0.5, delay: i * 0.1 }}
                                />
                              );
                            })}
                          </div>
                        </div>

                        {/* Action Button */}
                        <button className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white
                          py-2 rounded-lg font-semibold text-sm hover:from-blue-600 hover:to-blue-700
                          transition-all flex items-center justify-center gap-2">
                          View Full Details
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </motion.div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredClients.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-2xl p-12 text-center shadow-lg border border-gray-200"
        >
          <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Clients Found</h3>
          <p className="text-gray-600">Try adjusting your filters to see more results.</p>
        </motion.div>
      )}
    </div>
  );
};

export default ClientAnalyticsGrid;

